import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useContext, useMemo, useState } from "react";

import { badgeVariants } from "@/common/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/common/ui/tabs";
import { useSession } from "@/features/organization/useSession";
import { AuthContext } from "@/lib/auth-context";
import { cn } from "@/lib/utils";

import { AuditLogPanel } from "./AuditLogPanel";
import { BundleEditor } from "./BundleEditor";
import { GrantEditor } from "./GrantEditor";
import { ResolutionView } from "./ResolutionView";
import { buildScope, type EditableScopeKind } from "./scope";
import { ScopeSelector } from "./ScopeSelector";
import { useAuditLog } from "./use-audit-log";
import { useEffective } from "./use-effective";
import { useRegistry } from "./use-registry";
import {
  OfferingsTab,
  RequestQueueCard,
  RequestsTab,
  scopeRequests,
  ScopeStateTable,
  useOfferings,
  useRequestQueue,
  useScopeState,
  WorkspaceHeader,
  workspaceScope,
  WorkspaceSearch,
} from "./workspace";

/**
 * The operator entitlements console (ENR-1943), workspace-centric.
 *
 * ## One scope, one owner
 *
 * Every tab reads the SAME scope, and that scope has exactly one owner: the
 * `kind` + `id` pair below. The workspace search writes it, and the scope
 * selector writes it, and nothing else does. That is what makes the offering
 * cards and the raw grants table incapable of describing two different places
 * while sitting on one screen, which is the failure this page exists to end.
 *
 * The name beside the scope is dropped the moment the selector is touched: a
 * scope typed by hand is not the workspace that was searched for, and a header
 * still carrying the old name would be the page's one remaining way to lie.
 *
 * ## Writes
 *
 * The offering cards apply and retract through the bundle machinery (ENR-1944),
 * behind a confirmation, and every write refreshes `reloadKey` so the cards
 * re-derive from the scope-state read rather than from what the page believes
 * it wrote. Answering a request (ENR-1949) refreshes that key too, since an
 * approval IS a bundle apply, which is what makes the offering card and the
 * decided row two views of one reading rather than two claims.
 *
 * ## The queue is read once, for the whole page
 *
 * The store has no per-scope form of the pending queue, so both the
 * cross-workspace card above and the per-workspace tab below are views over
 * ONE platform-wide read. Two reads would be two audited cross-tenant
 * disclosures for one fact, and two sources that could disagree about it.
 */
export function EntitlementsPage(): React.ReactElement {
  const { capabilities, isLoading: registryLoading, error: registryError } = useRegistry();
  const { session } = useSession();
  const auth = useContext(AuthContext);

  /* The scope's single owner. `enterprise` with no id means "nothing selected
     yet", which is the state a workspace-centric page should open in. */
  const [kind, setKind] = useState<EditableScopeKind>("enterprise");
  const [id, setId] = useState("");
  const [workspace, setWorkspace] = useState<{ name: string; kind: string } | null>(null);

  const [capabilityId, setCapabilityId] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [queueKey, setQueueKey] = useState(0);
  const [tab, setTab] = useState("offerings");
  const [narrowed, setNarrowed] = useState(false);

  // Memoized because every read below keys on it; a scope rebuilt each render
  // would refetch forever.
  const scope = useMemo(() => buildScope(kind, id), [kind, id]);

  const offerings = useOfferings();
  const scopeState = useScopeState(scope, reloadKey);
  /* ONE platform-wide queue read for the whole page: the cross-workspace entry
     point and the per-workspace tab are two views of it, never two reads. Its
     key is separate from `reloadKey` because grant and bundle writes cannot
     move the request queue, and re-reading on one would file an extra audited
     pull of every tenant's pending requests. */
  const queue = useRequestQueue(queueKey);
  const requests = scopeRequests(queue.requests, scope);
  const { view, error: readError } = useEffective(scope, capabilityId, reloadKey);
  const logCapabilityId = narrowed && capabilityId !== "" ? capabilityId : null;
  const auditLog = useAuditLog(scope, logCapabilityId, reloadKey);

  const reload = (): void => {
    setReloadKey((previous) => previous + 1);
  };

  /* A decision moves the queue AND the entitlements underneath it, so both
     reads are refreshed. Two keys rather than one because the reverse is not
     true: a grant or bundle write cannot create, approve or deny a request, so
     re-reading the queue on one would file an extra audited pull of every
     tenant's pending requests for a change that could not have altered it. */
  const reloadAfterDecision = (): void => {
    setQueueKey((previous) => previous + 1);
    setReloadKey((previous) => previous + 1);
  };

  /* Selecting a workspace from the queue is the same act as searching for one,
     so it goes through the same single owner of the scope. The workspace NAME
     is cleared: a queue row carries the scope, not the name it was registered
     under, and a header still showing the previous workspace's name would be
     the page's one remaining way to lie. */
  const openFromQueue = (target: ScopeRef): void => {
    setKind(target.kind);
    setId(target.kind === "platform" ? "" : target.id);
    setWorkspace(null);
    setTab("requests");
  };

  /**
   * What the Requests tab is badged with.
   *
   * A capped read is the case the badge must NOT stay silent for: the queue is
   * platform-wide and filtered here, so a full page means this workspace's
   * requests may sit behind the cap. Rendering nothing then is precisely the
   * confident zero that stops an operator from opening the tab, so a capped
   * read badges `n+`, and a capped read that matched nothing badges `?`.
   */
  const pendingBadge = ((): { readonly text: string; readonly label: string } | null => {
    const count = requests?.length ?? null;
    if (count === null) return null;
    if (queue.capped) {
      return count === 0
        ? { text: "?", label: "Pending request count unknown, the queue read was capped" }
        : {
            text: `${String(count)}+`,
            label: `At least ${String(count)} pending requests, the queue read was capped`,
          };
    }
    return count === 0 ? null : { text: String(count), label: `${String(count)} pending requests` };
  })();

  return (
    <div className="space-y-6 p-6" data-testid="entitlements-page">
      <div>
        <h1 className="text-2xl font-semibold">Entitlements</h1>
        <p className="text-muted-foreground">
          What a workspace has, what it could have, and what is set underneath. Writes are
          staff-guarded and audited.
        </p>
      </div>

      {registryError !== null && (
        <p className="text-sm text-destructive" data-testid="registry-error">
          Failed to load capabilities: {registryError}
        </p>
      )}

      <RequestQueueCard
        requests={queue.requests}
        isLoading={queue.isLoading}
        error={queue.error}
        capped={queue.capped}
        maxAgeSeconds={queue.maxAgeSeconds}
        offerings={offerings.offerings ?? []}
        onOpen={openFromQueue}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Find a workspace</CardTitle>
            <CardDescription>
              Search by name. The scope id comes from the row you pick, never from typing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <WorkspaceSearch
              onSelect={(match) => {
                const next = workspaceScope(match);
                setKind(next.kind);
                setId(next.id);
                setWorkspace({ name: match.name, kind: match.kind });
              }}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Or target a scope directly</CardTitle>
            <CardDescription>
              The escape hatch for the scopes a workspace search cannot name: the platform root, an
              org unit, or a single user.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScopeSelector
              kind={kind}
              id={id}
              onKindChange={(next) => {
                setKind(next);
                setWorkspace(null);
              }}
              onIdChange={(next) => {
                setId(next);
                setWorkspace(null);
              }}
            />
          </CardContent>
        </Card>
      </div>

      {scope === null ? (
        <p className="text-sm text-muted-foreground" data-testid="no-scope-selected">
          Search for a workspace, or name a scope directly, to see what applies to it.
        </p>
      ) : (
        <>
          <WorkspaceHeader scope={scope} workspace={workspace} />

          <Tabs value={tab} onValueChange={setTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="offerings" data-testid="entitlements-tab-offerings">
                Offerings
              </TabsTrigger>
              <TabsTrigger value="requests" data-testid="entitlements-tab-requests">
                Requests
                {pendingBadge !== null && (
                  <>
                    {/* A span, not the Badge component: this sits inside Radix's
                        button trigger, and a div there is an invalid content
                        model. The number is decorative to a screen reader,
                        which gets the sentence below instead. */}
                    <span
                      className={cn(badgeVariants(), "ml-2")}
                      aria-hidden="true"
                      data-testid="requests-count-badge"
                    >
                      {pendingBadge.text}
                    </span>
                    <span className="sr-only">{pendingBadge.label}</span>
                  </>
                )}
              </TabsTrigger>
              <TabsTrigger value="raw" data-testid="entitlements-tab-raw">
                Raw grants
              </TabsTrigger>
              <TabsTrigger value="audit" data-testid="entitlements-tab-audit">
                Audit
              </TabsTrigger>
            </TabsList>

            <TabsContent value="offerings">
              {/* No branching here, by design. The tab owns the placeholder AND
                  both read errors, because anything this page swapped it for
                  would unmount the mutation hook during the re-read a write
                  triggers and discard the outcome of a privileged write. The
                  error branch was the worst of those: the write commits, the
                  re-read fails, and the operator is told only that the scope
                  could not be read. */}
              <OfferingsTab
                offerings={offerings.offerings}
                scope={scope}
                scopeState={scopeState.view}
                offeringsError={offerings.error}
                scopeStateError={scopeState.error}
                capabilities={capabilities}
                /* Empty until the session answers, which reads as "not
                   permitted" and disables the actions. Fail-closed is the
                   right default for a control that writes entitlements. */
                permissions={session?.permissions ?? []}
                onMutated={reload}
              />
            </TabsContent>

            <TabsContent value="requests">
              {/* No branching here, for the reason the Offerings tab has none:
                  anything this page swapped it for would take the outcome of a
                  decision off screen during the re-read that decision
                  triggered. The error branch is the worst of those, since the
                  write commits and the banner is then its only record. */}
              {/* Keyed on the scope, which DESTROYS an open confirmation and
                  any outcome when the page moves, rather than parking them.
                  A confirmation is a claim about one scope's rows: left
                  standing it would preview the new scope's state for a request
                  filed against the old one. Hiding it stops the wrong-target
                  press but leaves it to reappear on the way back; destroying
                  it cannot. The Offerings tab keys its grid for this reason. */}
              <RequestsTab
                key={scopeKey(scope)}
                requests={requests}
                isLoading={queue.isLoading}
                error={queue.error}
                capped={queue.capped}
                maxAgeSeconds={queue.maxAgeSeconds}
                offerings={offerings.offerings ?? []}
                scope={scope}
                scopeState={scopeState.view}
                capabilities={capabilities}
                /* Empty until the session answers, which reads as "not
                   permitted" and disables the controls. Fail-closed is the
                   right default for a control that writes entitlements. */
                permissions={session?.permissions ?? []}
                /* Read from the context directly rather than through `useAuth`,
                   which throws outside a provider. A surface that cannot name
                   the operator says so by wording a lost race neutrally, which
                   is better than refusing to render the page. */
                {...(auth === null ? {} : { operatorId: auth.user.sub })}
                onDecided={reloadAfterDecision}
              />
            </TabsContent>

            <TabsContent value="raw" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Grant</CardTitle>
                  <CardDescription>
                    {registryLoading ? "Loading capabilities" : "Set or clear a capability value."}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <GrantEditor
                    scope={scope}
                    capabilities={capabilities}
                    capabilityId={capabilityId}
                    onCapabilityChange={setCapabilityId}
                    onMutated={reload}
                    granted={view?.granted ?? null}
                    grantedKnown={view !== null}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Bundle</CardTitle>
                  <CardDescription>
                    Apply or retract a versioned template at this scope. Bundle-sourced grants rank
                    below an override here, so applying a tier cannot undo a deliberate exception.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <BundleEditor scope={scope} onMutated={reload} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Effective vs granted</CardTitle>
                  <CardDescription>
                    What is set at this scope, what actually applies, and why they differ.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResolutionView
                    view={view}
                    error={readError}
                    hasSelection={capabilityId !== ""}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Everything at this scope</CardTitle>
                  <CardDescription>
                    Every stored row, with what the resolution path does with it. The same read the
                    offering cards derive their state from.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {scopeState.error !== null ? (
                    <p className="text-sm text-destructive" data-testid="scope-state-read-error">
                      Failed to read this scope: {scopeState.error}
                    </p>
                  ) : scopeState.view === null ? (
                    <p className="text-sm text-muted-foreground" data-testid="scope-state-loading">
                      Reading this scope…
                    </p>
                  ) : (
                    <ScopeStateTable view={scopeState.view} capabilities={capabilities} />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit">
              <Card>
                <CardHeader>
                  <CardTitle>Audit log</CardTitle>
                  <CardDescription>
                    Every change recorded at this scope, newest first. Append-only: there is no edit
                    or delete path.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <AuditLogPanel
                    entries={auditLog.entries}
                    isLoading={auditLog.isLoading}
                    error={auditLog.error}
                    hasMore={auditLog.hasMore}
                    unreadable={auditLog.unreadable}
                    onLoadMore={auditLog.loadMore}
                    hasScope
                    capabilityId={capabilityId === "" ? null : capabilityId}
                    narrowed={narrowed}
                    onNarrowedChange={setNarrowed}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
